import { CommonModule } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Button } from '@shared/button/button';
import { Icon } from '@shared/icon/icon';
import { Badge } from '@shared/badge/badge';
import { Pagination } from '@shared/pagination/pagination';
import { Skeleton } from '@directives/skeleton';
import { CSSValuePipe } from '@pipes/css-value-pipe';
import { Switch } from '@shared/switch/switch';
import { isChildOf } from '@helpers';
import type {
  PaginationResult,
  TableColumn,
  TableRowSelected,
  TableSortingDirection,
} from '@appTypes';

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [CommonModule, CSSValuePipe, Skeleton, Icon, Pagination, Badge, Button, Switch, MatTooltipModule],
  templateUrl: './table.html',
  styleUrl: './table.scss',
})
export class Table<T extends Record<string, unknown>> {
  // Inputs
  $columns = input.required<TableColumn<T>[]>({ alias: 'columns' });
  $rows = input.required<T[]>({ alias: 'rows' });
  $footRow = input<T | null>(null, { alias: 'footRow' });
  $tableStyle = input<'classic' | 'modern'>('modern', { alias: 'tableStyle' });
  $zebra = input<boolean>(false, { alias: 'zebra' });
  $headHeight = input<string>('4.2rem', { alias: 'headHeight' });
  $rowsHeight = input<string>('5.8rem', { alias: 'rowsHeight' });
  $footHeight = input<string>('4.2rem', { alias: 'footHeight' });
  $headUppercase = input<boolean>(true, { alias: 'headUppercase' });
  $footUppercase = input<boolean>(true, { alias: 'footUppercase' });
  $firstColumnSticky = input<boolean>(false, { alias: 'firstColumnSticky' });
  $allowRowSelection = input<boolean>(false, { alias: 'allowRowSelection' });
  $itemsPerPage = input<number>(10, { alias: 'itemsPerPage' } );
  $loading = input<boolean>(false, { alias: 'loading' });
  $loadingSkeleton = input<boolean | 'translucent'>('translucent', { alias: 'loadingSkeleton' });

  // Outputs
  rowSelected = output<TableRowSelected<T> | null>();

  // Signals / atributos
  $sortingDirection = signal<TableSortingDirection>('asc');
  $lastSortingColumnKey = signal<string | number | symbol>('');
  $selectedRow = signal<T | null>(null);
  // Filas paginadas
  private paginatedRows: T[] = [];

  // Filas ordenadas
  sortedRows: T[] = [];

  // Signals computados
  $pagesAmount = computed<number>(() => Math.ceil(this.$rows().length / this.$itemsPerPage()));

  $tableHeight = computed<string | null>(() => {
    if (this.$itemsPerPage() < 0 || this.$pagesAmount() === 1 && this.$rows().length > 0)
      return null;
    return `calc(${ this.$headHeight() } + ${ this.$rowsHeight() } * ${ this.$itemsPerPage() } + ${ this.$footRow() === null ? '0px' : this.$footHeight() } + 13px)`;
  });

  $firstColumnIndex = computed<number>(() => {
    for (let i = 0; i < this.$columns().length; i++)
      if (this.$columns()[i].hide !== true)
        return i;
    return 0;
  });

  $lastColumnIndex = computed<number>(() => {
    for (let i = this.$columns().length - 1; i >= 0; i--)
      if (this.$columns()[i].hide !== true)
        return i;
    return Math.max(0, this.$columns.length - 1);
  });

  // Maneja el evento de selección de fila
  onRowSelected(event: PointerEvent, row: T, column: TableColumn<T>, value: T[keyof T]) {
    if (isChildOf(event.target as HTMLElement, 'app-button', 'app-switch'))
      return;

    if (row === this.$selectedRow()) {
      this.$selectedRow.set(null);
      this.rowSelected.emit(null);
      return;
    }
    this.$selectedRow.set(row);
    this.rowSelected.emit({ row, column, value });
  }

  // Maneja la paginación
  onPagination(paginationResult: PaginationResult<T>) {
    this.paginatedRows = paginationResult.items;
    this.sortedRows = this.paginatedRows;
    this.$lastSortingColumnKey.set('');
    this.$sortingDirection.set('asc');
  }

  // Maneja el ordenamiento
  onSort(column: TableColumn<T>) {
    if (this.$lastSortingColumnKey() !== column.key)
      this.$sortingDirection.set('asc');

    this.$lastSortingColumnKey.set(column.key);

    this.sortedRows.sort((a, b) => {
      let valueA = a[column.key];
      let valueB = b[column.key];

      // Ordenar strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        let alphanumValueA: string | number = valueA;
        let alphanumValueB: string | number = valueB;

        // Intentar ordenar como numbers
        try {
          const numValueA = Number(valueA.toString().replaceAll(',', ''));
          const numValueB = Number(valueB.toString().replaceAll(',', ''));
          if (!isNaN(numValueA) && !isNaN(numValueB)) {
            alphanumValueA = numValueA;
            alphanumValueB = numValueB;
          }
        }
        catch {}
        if (this.$sortingDirection() === 'asc')
          return alphanumValueA > alphanumValueB ? 1 : -1;
        return alphanumValueA < alphanumValueB ? 1 : -1;
      }

      // Ordenar numbers
      if (typeof valueA === 'number' || typeof valueB === 'number') {
        if (this.$sortingDirection() === 'asc')
          return valueA > valueB ? 1 : -1;
        return valueA < valueB ? 1 : -1;
      }

      // Ordenar booleans
      if (typeof valueA === 'boolean' || typeof valueB === 'boolean') {
        console.log('Por boolean');

        if (this.$sortingDirection() === 'asc')
          return valueA > valueB ? -1 : 1;
        return valueA < valueB ? -1 : 1;
      }

      // Por defecto, no ordenar
      return 0;
    });

    // Actualizar orden
    if (this.$sortingDirection() === 'asc')
      this.$sortingDirection.set('desc');
    else
      this.$sortingDirection.set('asc');
  }
}
